import React, { FC, useContext, useState } from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Badge, Col, DatePicker, DatePickerProps, Input, message, Row, Select, Space, Spin, Tooltip } from 'antd';
import moment from 'moment';
import { api, endpoints } from '../../../../api';
import {
  ConfirmSvg,
  EditIconSvg,
  EpertiseExpertConfirmedSvg,
  ExpertiseComplete,
  ExpertiseInWorkSvg,
  ExpertiseRefuseSvg,
  TextbookExpertiseNotStartedSvg,
} from '../../../../icons';
import { authContext } from '../../../../store/authStore/AuthContext';
import {
  CustomButton,
  CustomModal,
  CustomSelect,
  FormInput,
  FormItem,
  GridItem,
  NoButtonContainer,
} from '../../../../styles/styled';
import { StatusType } from '../../../ApplicationPage/types';
import { ExpertsType } from '../../../NewExpertPage/types';
import { ExpertiseTextbookType, TextbookExpertisesType } from '../../types';
import { ExpertsListContainer, ExpertTableBlock } from './styled';
import { badgeStyle, ExpertiseLoadingStatusType, ReplaceExpertInputType } from './types';

type ExpertsListComponentProps = {
  experts: TextbookExpertisesType;
  textbookStatus?: StatusType;
  textbookId?: number;
  textbook?: ExpertiseTextbookType;
  setLoad: () => void;
};

const { TextArea } = Input;

export const ExpertsListComponent: FC<ExpertsListComponentProps> = ({
  experts,
  textbook,
  textbookId,
  textbookStatus,
  setLoad,
}) => {
  const { state } = useContext(authContext);
  const [open, setOpen] = useState<boolean>(false);
  const [openConfirmModal, setOpenConfirmModal] = useState<boolean>(false);
  const [expertsCountState, setExpertsCount] = useState<number>(3);
  const [openDateModal, setOpenDateModal] = useState<boolean>(false);
  const [dateState, setDate] = useState<string>('');
  const [currentNotifyExpert, setCurrentNotifyExpert] = useState<number | undefined>();
  const [changeDateSendNotification, setChangeDateNotificationModal] = useState<boolean>(false);
  const [changeCreatedAtState, setChangeCreatedAt] = useState<boolean>(false);
  const [replaceExpertState, setReplaceExpert] = useState<boolean>(false);
  const [replaceExpertInputs, setReplaceExpertInputs] = useState<ReplaceExpertInputType>({});
  const [expertsSearchState, setExpertsSearchState] = useState<ExpertsType>([]);
  const [expertSelectorInputState, setExpertSelectorInput] = useState<string>('');
  const [commentModalState, setCommentModalState] = useState<string>();
  const [expertNotifyStatus, setExpertNotifyStatus] = useState<ExpertiseLoadingStatusType>({ status: 'neutral' });
  const [replaceExpertIdState, setReplaceExpertId] = useState<number>();

  const onChange: DatePickerProps['onChange'] = (date, dateString) => {
    setDate(dateString);
  };

  const { Option } = Select;

  const updateTextbookExpertCounts = async (id: number, experts_count_number: number) => {
    try {
      const response = await api.put(endpoints.getTextbookById(id), { experts_count: experts_count_number });

      if (response.status === 200) {
        await api.post(endpoints.expertiseAddExpert(id)).catch((error) => message.error(error.response.data.detail));
      }

      setLoad();
    } catch (error) {
      message.error('Ошибка').then();
    }
  };

  const findExpertsByFio = (fio: string) => {
    api
      .get<{ experts: ExpertsType }>(endpoints.getExperts({ perPage: 1000, fio: fio, status: 3 }))
      .then((el) => setExpertsSearchState(el.data.experts));
  };

  const confirmExpert = (id?: number) => {
    if (id) {
      setExpertNotifyStatus({ id: id, status: 'loading' });

      api
        .put(endpoints.updateExpertiseExpert(id))
        .then(() => {
          setOpenConfirmModal(false);
          message.success('Эксперт подтвержден');
          setLoad();

          setExpertNotifyStatus({ ...expertNotifyStatus, status: 'neutral' });
        })
        .catch(() => message.error('Произошла ошибка при подтверждении эксперта'));
    }
  };

  const replaceExpertFunc = (expertiseId: number, expertId: number) => {
    api
      .post(endpoints.expertiseChangeExpert(expertiseId, expertId), {
        reason: replaceExpertInputs.reason || '',
      })
      .then((el) => el.request.status === 200 && setOpenConfirmModal(true))
      .catch(() => message.error('Ошибка'))
      .finally(() => message.success('Завершено'));
  };

  const notifyExpert = (id?: number, date?: string, changeNotification?: boolean, changeCreatedAt?: boolean) => {
    if (id) {
      api
        .put(
          changeNotification
            ? endpoints.expertiseChangeDate(id)
            : changeCreatedAt
            ? endpoints.expertiseChangeCreatedAt(id)
            : endpoints.expertiseAddDate(id),
          { date: date },
        )
        .then(() => {
          message.success('Уведомление отправлено');

          setLoad();

          setOpenConfirmModal(false);
        })
        .catch(() => 'Произошла ошибка при отправке даты уведомления');
    }
  };

  const addDaysToDate = (days: number, date: string): string => {
    const newDate = moment(date, 'YYYY-MM-DD').add(days, 'days').format('DD.MM.YYYY');

    return newDate.toString();
  };

  const expertiseStatusDistribute = (status: string, id: number): JSX.Element => {
    switch (status) {
      case 'not_started':
        return <TextbookExpertiseNotStartedSvg />;
      case 'in_work':
        return <ExpertiseInWorkSvg />;
      case 'ready':
        return <EpertiseExpertConfirmedSvg />;
      case 'complete':
        return <ExpertiseComplete />;
      case 'refuse':
        return <ExpertiseRefuseSvg />;
      case 'decline':
        return <ExpertiseRefuseSvg />;
      default:
        return expertNotifyStatus.status === 'loading' && id === expertNotifyStatus.id ? (
          <Spin size="small" />
        ) : !state.isSuperFederal ? (
          <CustomButton
            onClick={() => {
              setOpenConfirmModal(true);
              setCurrentNotifyExpert(id);
            }}
            lined
            padding="4px 6px"
            fontsize="12px !important"
            width="120px"
          >
            Подтвердить
          </CustomButton>
        ) : (
          <CustomButton
            onClick={() => {
              setOpenConfirmModal(true);
              setCurrentNotifyExpert(id);
            }}
            lined
            padding="0 10px"
            fontsize="12px !important"
            width="120px"
          >
            <ConfirmSvg />
          </CustomButton>
        );
    }
  };

  return (
    <>
      {textbook?.expertises?.length === 0 && textbookStatus?.code === 'distribution' && (
        <GridItem align="flex-end" justify="flex-end">
          <CustomButton onClick={() => setOpen(true)} width="200px" padding="5px 10px" fontsize="10px !important" lined>
            Добавить экспертов
          </CustomButton>
        </GridItem>
      )}
      <CustomModal
        title="Добавить экспертов"
        centered
        okText="Добавить"
        visible={open}
        onOk={() => {
          setOpen(false);
          if (textbookId) updateTextbookExpertCounts(textbookId, expertsCountState);
        }}
        onCancel={() => setOpen(false)}
        width={600}
      >
        <CustomSelect value={expertsCountState} onChange={(value) => setExpertsCount(value as number)}>
          <Option key={3} value={3}>
            3
          </Option>
          <Option key={4} value={4}>
            4
          </Option>
          <Option key={5} value={5}>
            5
          </Option>
          <Option key={6} value={6}>
            6
          </Option>
          <Option key={7} value={7}>
            7
          </Option>
          <Option key={8} value={8}>
            8
          </Option>
        </CustomSelect>
      </CustomModal>
      <br />
      <CustomModal
        centered
        visible={openConfirmModal}
        onCancel={() => setOpenConfirmModal(false)}
        okText={
          expertNotifyStatus?.status === 'loading' ? 'Подождите уведомление отправляется' : 'Отправить уведомление'
        }
        okButtonProps={{ disabled: expertNotifyStatus?.status === 'loading' }}
        onOk={() => {
          confirmExpert(currentNotifyExpert);
        }}
        width={800}
      >
        <Row gutter={[15, 15]}>
          <Col span={24}>
            <GridItem justify="center" align="center" gap="0">
              <div className="modal-title">Уведомление об участии в экспертизе учебника</div>
            </GridItem>
          </Col>
          <Col span={24}>
            <GridItem justify="center" align="center" gap="0">
              <div className="for-expert-layout">Уважаемый эксперт!</div>
              <div className="for-expert-layout">Вы отобраны для участия в проведении экспертизы учебника:</div>
            </GridItem>
          </Col>
          <Col span={24}>
            <FormItem>
              <div className="dark-text">Наименование учебника</div>
              <div className="text no-padding">{textbook?.title}</div>
            </FormItem>
          </Col>

          <Col span={12}>
            <FormItem>
              <div className="dark-text">Автор (авторский коллектив) учебника</div>
              <div className="text no-padding">{textbook?.author}</div>
            </FormItem>
          </Col>
          <Col span={12}>
            <FormItem>
              <div className="dark-text">Класс</div>
              <div className="text no-padding">{textbook?.school_classes[0]?.title}</div>
            </FormItem>
          </Col>
          <Col span={24}>
            <FormItem>
              <div className="dark-text">Наименование издательства</div>
              <div className="text no-padding">{textbook?.line_of_textbooks?.application.publisher?.title}</div>
            </FormItem>
          </Col>
          {textbook?.right_holder && textbook.right_holder.length > 0 && (
            <Col span={24}>
              <FormItem>
                <div className="dark-text">
                  Правообладатель (наименование юридического лица либо фамилия, имя, отчество (при наличии) физического
                  лица, которому в установленном законодательством Российской Федерации порядке принадлежит
                  исключительно право на учебник)
                </div>
                <div className="text no-padding">{textbook.right_holder}</div>
              </FormItem>
            </Col>
          )}
          <Col span={24}>
            <FormItem>
              <div className="dark-text">
                Язык издания учебника (указывается для учебников, изданных на государственных языках республик или на
                языках народов Российской Федерации)
              </div>
              <div className="text no-padding">{textbook?.language?.title || 'Отсутствует'}</div>
            </FormItem>
          </Col>
          <Col span={24}>
            <FormItem>
              <div className="dark-text">
                Возможность использования учебника при реализации адаптированных общеобразовательных программ, а также
                общеобразовательных программ с углубленным изучением отдельных учебных предметов, предметных областей
                соответствующей образовательной программы
              </div>
              <div className="text no-padding">{textbook?.adaptive ? 'Да' : 'Нет'}</div>
            </FormItem>
          </Col>
          <Col span={24}>
            <FormItem>
              <div className="dark-text">
                Срок предоставления письменного согласия эксперта на участие в проведении соответствующей экспертизы
                учебника и разработанных в комплекте с ним учебных пособий – <strong>5 календарных дней</strong>.
              </div>
            </FormItem>
          </Col>
        </Row>
      </CustomModal>

      <CustomModal
        title="Причина отказа"
        centered
        footer={false}
        visible={!!commentModalState}
        onCancel={() => setCommentModalState(undefined)}
        width={600}
      >
        <FormItem>
          <div className="dark-text">{commentModalState}</div>
        </FormItem>
      </CustomModal>
      <CustomModal
        title="ЗАМЕНА ЭКСПЕРТА"
        centered
        visible={replaceExpertState}
        onCancel={() => setReplaceExpert(false)}
        okText="Заменить и отправить уведомление"
        onOk={() => {
          setReplaceExpert(false);
          if (replaceExpertIdState && currentNotifyExpert) replaceExpertFunc(currentNotifyExpert, replaceExpertIdState);
          // notifyExpert(currentNotifyExpert);
        }}
        width={600}
      >
        <Row gutter={[20, 20]}>
          <Col span={24}>
            <FormItem>
              <div className="dark-text">Укажите причину замены</div>
              <TextArea
                value={replaceExpertInputs?.reason}
                onChange={(e) => setReplaceExpertInputs({ ...replaceExpertInputs, reason: e.target.value })}
              />
            </FormItem>
          </Col>
          <Col span={24}>
            <FormItem>
              <div className="dark-text">
                Выберите эксперта
                <Tooltip placement="top" title="Введите фамилию, чтобы заполнить селектор экспертами">
                  <Badge count={<InfoCircleOutlined style={badgeStyle} />} />
                </Tooltip>
              </div>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <GridItem direction="row" justify="flex-start" align="flex-start" gap="15px" wrap="nowrap">
                  <FormInput
                    placeholder="Введите фамилию"
                    onChange={(e) => setExpertSelectorInput(e.target.value)}
                    value={expertSelectorInputState}
                    width="100%"
                  />
                  <CustomButton onClick={() => findExpertsByFio(expertSelectorInputState)} lined padding="6px 15px">
                    Найти
                  </CustomButton>
                </GridItem>

                <CustomSelect
                  onChange={(value) => setReplaceExpertId(value as number)}
                  showSearch
                  width="100% !important"
                  placeholder
                >
                  {expertsSearchState.map((el) => {
                    return (
                      <Option key={el.id} label={el.first_name}>
                        {el.last_name} {el.first_name} {el.patronymic} ({el.id})
                      </Option>
                    );
                  })}
                </CustomSelect>
              </Space>
            </FormItem>
          </Col>
        </Row>
      </CustomModal>
      <CustomModal
        title="Добавить дату получения согласия на экспертизу"
        centered
        visible={openDateModal || changeDateSendNotification || changeCreatedAtState}
        okText="Сохранить"
        onCancel={() => {
          setOpenDateModal(false);
          setChangeDateNotificationModal(false);
          setChangeCreatedAt(false);
        }}
        onOk={() => {
          setOpenDateModal(false);
          setChangeDateNotificationModal(false);
          setChangeCreatedAt(false);
          notifyExpert(currentNotifyExpert, dateState, changeDateSendNotification, changeCreatedAtState);
        }}
        width={450}
      >
        <DatePicker onChange={onChange} format="DD.MM.YYYY" style={{ width: '100%' }} />
      </CustomModal>
      <ExpertsListContainer>
        <ExpertTableBlock text="bold">Дата распределения</ExpertTableBlock>
        <ExpertTableBlock text="bold">ФИО</ExpertTableBlock>
        <ExpertTableBlock text="bold">Статус экспертизы</ExpertTableBlock>
        <ExpertTableBlock text="bold">Дата отправки уведомления</ExpertTableBlock>
        <ExpertTableBlock text="bold">Дата получения согласия</ExpertTableBlock>
        <ExpertTableBlock text="bold">Сроки проведения экспертизы</ExpertTableBlock>
        <ExpertTableBlock text="bold">Примечания</ExpertTableBlock>
      </ExpertsListContainer>
      {experts.map((el, index) => {
        return (
          <ExpertsListContainer key={index}>
            <ExpertTableBlock>
              {state.isSuperFederal && (
                <CustomButton
                  onClick={() => {
                    setChangeCreatedAt(true);
                    setCurrentNotifyExpert(el.id);
                  }}
                  lined
                  width="20px"
                  padding="5px 12px"
                >
                  <div className="icon">
                    <EditIconSvg />
                  </div>
                </CustomButton>
              )}
              {moment(el?.created_at?.split('T')[0]).format('DD.MM.YYYY')}
            </ExpertTableBlock>
            <ExpertTableBlock>
              {!el?.expert?.povs
                ? el.expert.last_name + ' ' + el.expert.first_name + ' ' + el.expert.patronymic
                : el?.expert?.region?.title}
            </ExpertTableBlock>
            <ExpertTableBlock center={!state.isSuperFederal} spaceBetween={state.isSuperFederal}>
              {state.isSuperFederal && el?.status?.code !== 'decline' && (
                <CustomButton
                  onClick={() => {
                    setReplaceExpert(true);
                    setCurrentNotifyExpert(el.id);
                  }}
                  lined
                  padding="10px 10px"
                  fontsize="12px !important"
                  width="120px"
                >
                  <div className="medium">
                    <EditIconSvg />
                  </div>
                </CustomButton>
              )}
              {expertiseStatusDistribute(el?.status?.code, el.id || 0)}
            </ExpertTableBlock>
            <ExpertTableBlock>
              {state.isSuperFederal && (
                <CustomButton
                  onClick={() => {
                    setChangeDateNotificationModal(true);
                    setCurrentNotifyExpert(el.id);
                  }}
                  lined
                  width="20px"
                  padding="5px 12px"
                >
                  <div className="icon">
                    {' '}
                    <EditIconSvg />
                  </div>
                </CustomButton>
              )}
              {el.date_send_notification?.split('T')[0].split('-').reverse().join('.')}
            </ExpertTableBlock>
            <ExpertTableBlock center text="bold">
              {state.isSuperFederal && el?.status?.code !== 'decline' && el?.consent_expert_date && (
                <CustomButton lined width="20px" padding="5px 12px">
                  <div className="icon">
                    <EditIconSvg />
                  </div>
                </CustomButton>
              )}
              {el?.status?.code === 'ready' && !el.consent_expert_date && (
                <CustomButton
                  onClick={() => {
                    setOpenDateModal(true);
                    setCurrentNotifyExpert(el.id);
                  }}
                  lined
                  padding="4px 20px"
                  fontsize="12px !important"
                  width="120px"
                >
                  Добавить
                </CustomButton>
              )}
              {el?.consent_expert_date && moment(el?.consent_expert_date?.split('T')[0]).format('DD.MM.YYYY')}
            </ExpertTableBlock>
            <ExpertTableBlock>
              {el?.consent_expert_date && addDaysToDate(45, el.consent_expert_date.split('T')[0])}
            </ExpertTableBlock>
            <ExpertTableBlock>
              {el?.status?.code === 'decline' && (
                <NoButtonContainer>
                  <button className="link" onClick={() => setCommentModalState(el?.reason_decline_federal)}>
                    Причина отказа
                  </button>
                </NoButtonContainer>
              )}
            </ExpertTableBlock>
          </ExpertsListContainer>
        );
      })}
    </>
  );
};
